/* isCategoryMatch — the IS-category keyword suggester (moved verbatim from AdminPage.js so it is
 * ONE function importable by both the Step-3 grid and the per-dg payroll back-fill's cat_slug
 * fallback (Build C, D4). Returns display form: 'COGS' | 'S&M' | 'G&A' | 'R&D' | 'Other' | 'Skip' | ''. */
/* The canonical IS section labels, in statement order. AdminPage's IS_CATEGORIES is
 * ['Skip', ...IS_CATEGORY_LABELS] — ONE list, so the dropdown, the render sort and the
 * direct-map below cannot drift apart (a third hardcoded list is how the line-key
 * taxonomy went wrong, §12.5 B3-8).
 * ★ 'Revenue' is deliberately NOT a member: a template revenue row must resolve to
 * 'Skip' here and be wired at Step 7. It therefore falls through the direct-map into
 * the keyword rules, where /revenue/ returns 'Skip'. */
export const IS_CATEGORY_LABELS = ['COGS', 'S&M', 'G&A', 'R&D', 'Other'];

/* The IS TEMPLATE's section column accepts the five labels above plus "Revenue" (which Step 3 turns into
 * Skip, wired at Step 7). ONE spelling of "Revenue", used by templateISCategory below and by the template
 * spec (templateSpecs.js), so the documented list and the direct-map cannot disagree. */
export const IS_REVENUE_SECTION = 'Revenue';
export const IS_TEMPLATE_SECTIONS = [IS_REVENUE_SECTION, ...IS_CATEGORY_LABELS];

/* The sections a DEPARTMENT's payroll can land in (Step 5): the five labels without "Other". Moved here from
 * AdminPage's page-local DEPT_IS_CATEGORIES (2026-09-29) so the employee template spec can import it. */
export const PAYROLL_IS_CATEGORY_LABELS = IS_CATEGORY_LABELS.filter(c => c !== 'Other');

/* The TEMPLATE's own answer for a section column, or null (2026-09-24). An exact canonical
 * label is the category itself; the template's "Revenue" section is Skip (wired at Step 7).
 * Step 3 shows a category that came from here as DECIDED -- it is the operator's own
 * spreadsheet -- and one that came from the keyword rules below as a SUGGESTION. It is also
 * matchISCategory's first step, so the two cannot disagree about what a template label is. */
export function templateISCategory(text) {
    const raw = (text || '').trim().toLowerCase();
    if (!raw) return null;
    const direct = IS_CATEGORY_LABELS.find(c => c.toLowerCase() === raw);
    if (direct) return direct;
    if (raw === IS_REVENUE_SECTION.toLowerCase()) return 'Skip';
    return null;
}

export function matchISCategory(text) {
    /* ★ Direct-map a verbatim canonical category label BEFORE the keyword rules — the IS
     * analogue of matchBSCategory's fix (90244a6, AdminPage.js:558-572), which never got
     * applied here.
     *
     * The canonical IS template carries the section name in its is_category column, and
     * parseFinancialExcel writes it into historical_gl_lines.gl_category — the first
     * argument suggestISCategory passes in. The keyword rules below recognize ACCOUNT
     * words (cost of / marketing / consultant / …), NOT section labels, so of the six
     * labels a canonical template actually uses only three matched, and those three
     * ("COGS", "R&D", "Revenue") matched BY ACCIDENT — their literal text happens to
     * appear inside unrelated keyword patterns. "S&M", "G&A" and "Other" matched nothing
     * and fell through to a guess from the GL line's NAME, which is how "Consultants"
     * (template S&M) became R&D and 9 lines came up with no category at all.
     *
     * An exact label is authoritative — trust it. 'Skip' is intentionally not
     * direct-mapped (it never appears as a template gl_category; a skipped line is
     * simply absent), and neither is 'Revenue' (see IS_CATEGORY_LABELS above). */
    const tpl = templateISCategory(text);
    if (tpl) return tpl;   // an exact label -- and "Revenue", which the keyword rules below also send to Skip
    const l = (text || '').toLowerCase();
    /* ★ TOKENS TIGHTENED. The COGS pattern used to contain the bare tokens `data`, `support`
     * and `customer success`, which made it confidently WRONG on real department names:
     *     "Data Science"                  -> COGS   (should be S&M)   — the bare `data`
     *     "Client Success, Support & Imp" -> COGS   (should be S&M)   — `support`
     * A greedy token is worse than no token: a blank suggestion makes the operator think,
     * whereas a confident wrong one gets accepted, and a department's whole payroll lands in
     * the wrong section. `data` now only matches as part of a real COGS phrase (data centre /
     * data transfer / data warehouse), and Data Science / Client Success are matched
     * explicitly by S&M below (which is checked first for them via their own names).
     * Harmless for cid=3 (those rows are deliberately unmapped) — this is for tenant #2. */
    if (/\bdata (cent(er|re)|transfer|warehouse)\b/i.test(l)) return 'COGS';
    if (/\b(data science|client success|customer success)\b/i.test(l)) return 'S&M';
    if (/cost of|cogs|merchant|hosting|isp|infrastructure|implementation|cloud|aws|\bserver\b|processing fee|platform fee|payment processing|gateway|bandwidth/i.test(l)) return 'COGS';
    if (/sales.*(marketing|&)|marketing|advertising|commission|business development|revenue operations|travel|meals|entertainment|conferences|events|tradeshows|pr\b|public relations|brand|demand generation|lead generation|sponsorship/i.test(l)) return 'S&M';
    if (/general|administrative|legal|accounting|finance|office|rent|dues|subscription|insurance|professional services|recruiting|hr\b|facilities|utilities|telephone|internet|postage|printing|bank fee|banking|wire fee|transaction fee/i.test(l)) return 'G&A';
    if (/research|development|engineering|product|r&d|technology|software dev|contractors|consultant|tools|licenses|developer|qa\b|quality assurance|testing/i.test(l)) return 'R&D';
    if (/income tax|provision for tax|interest expense|interest income|other income|other expense|depreciation|amortization|non-operating/i.test(l)) return 'Other';
    if (/revenue|income|sales|refund|discount|billable/i.test(l)) return 'Skip';
    return '';
}
